import { Button, Card, Grid, Group, Stack, Title } from '@mantine/core'
import { isInRange, useForm } from '@mantine/form'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDeploymentStepperContext } from '@/components/deployments/deployment-editor/DeploymentStepperContext'
import { StepSection } from '@/components/deployments/deployment-editor/StepSection'
import {
  useGetMultipleDeviceDetails,
  useGetSavedVlmGpus
} from '@/queries/deviceQueries'
import { NormalizedStreamBusinessLogic } from '@/types/businessLogic'
import {
  BlurOptions,
  ConfidenceThreshold,
  GpuSelection,
  InputSourceType,
  OcrSettings as OcrSettingsType
} from '@/types/deployment'
import { NotificationSettings } from '@/types/notificationSettings'
import {
  MAX_CACHE_SECONDS,
  MAX_EVENT_VIDEO_DURATION_TIME,
  MAX_PRE_EVENT_SECONDS,
  MIN_CACHE_SECONDS,
  MIN_EVENT_VIDEO_DURATION_TIME,
  MIN_PRE_EVENT_SECONDS
} from '../LogicSelection/LogicHandler/constants'
import {
  extractConfidenceThresholdsFromBusinessLogic,
  extractConfidenceThresholdsFromLabels
} from '../helpers/extractConfidenceThresholds'
import {
  GpuSelectionState,
  RunningGpus,
  getGpuSelectionError,
  getGpuSelectionState,
  sanitizeGpuSelection
} from '../helpers/gpuSelection'
import { BlurSettings } from './BlurSettings/BlurSettings'
import { CameraGroupSettings } from './CameraGroupSettings/CameraGroupSettings'
import { ConfidenceSettings } from './DetectionConfidence/ConfidenceSettings'
import { DetectionSettings } from './DetectionSettings/DetectionSettings'
import { EventRecordingSettings } from './EventRecording/EventRecordingSettings'
import { GpuSettings } from './GpuSettings/GpuSettings'
import { GroupGpuSettings } from './GpuSettings/GroupGpuSettings'
import { NotificationSettingsHandler } from './NotificationSettings/NotificationSettingsHandler'
import { OcrSettings } from './OcrSettings/OcrSettings'
import { SettingsCard } from './SettingsCard'

const validateOcrList = (
  value: string[],
  errorMessage: string,
  enabled?: boolean
) => {
  if (value.length > 0 && enabled) {
    return value.every((item) => /^[a-zA-Z0-9]+$/.test(item))
      ? null
      : errorMessage
  }

  return null
}

export type FormValues = {
  confidenceThresholds: ConfidenceThreshold[]
  blurOptions: BlurOptions
  eventVideoDurationTime: number
  preEventSeconds: number
  cacheSeconds: number | ''
  inferenceInterval: number
  minDetectionFrames: number
  notificationSettings: NotificationSettings
  ocrSettings: OcrSettingsType
  gpuIndex: number | null
  runInGroups: boolean
  gpuSelection: GpuSelection
}

type DeploymentSettingsProps = {
  initialConfidenceThresholds?: ConfidenceThreshold[]
  savedStreamLogics: NormalizedStreamBusinessLogic[]
  blurOptions: BlurOptions
  eventVideoDurationTime: number
  preEventSeconds: number
  cacheSeconds: number | ''
  inferenceInterval: number
  minDetectionFrames: number
  notificationSettings: NotificationSettings
  hasEmailAlertEnabled?: boolean
  hasOCREnabled?: boolean
  ocrSettings: OcrSettingsType
  inputSource: InputSourceType
  selectedDeviceIds: string[]
  cameraDeviceIds: string[]
  initialGpuIndex: number | null
  initialRunInGroups: boolean
  initialGpuSelection: GpuSelection
  runningGpus?: RunningGpus
  onGoBack: () => void
  onContinue: (values: FormValues) => void
}

export const DeploymentSettings = ({
  initialConfidenceThresholds = [],
  savedStreamLogics,
  blurOptions,
  eventVideoDurationTime,
  preEventSeconds,
  cacheSeconds,
  inferenceInterval,
  minDetectionFrames,
  notificationSettings,
  hasEmailAlertEnabled,
  hasOCREnabled,
  ocrSettings,
  inputSource,
  selectedDeviceIds,
  cameraDeviceIds,
  initialGpuIndex,
  initialRunInGroups,
  initialGpuSelection,
  runningGpus,
  onGoBack,
  onContinue
}: DeploymentSettingsProps) => {
  const intl = useIntl()
  const { uniqueLabels } = useDeploymentStepperContext()
  const isCamera = inputSource === InputSourceType.Cameras

  const {
    data: devices,
    isLoading: isLoadingDevices,
    isError: devicesLoadFailed,
    refetch: reloadDevices
  } = useGetMultipleDeviceDetails(cameraDeviceIds, isCamera)
  const { data: savedVlmGpus, isLoading: isLoadingSavedVlmGpus } =
    useGetSavedVlmGpus(cameraDeviceIds, isCamera)

  const gpuMessages: Record<NonNullable<GpuSelectionState>, string> = {
    loading: 'deployments.gpuSettings.loading',
    loadFailed: 'deployments.gpuSettings.loadFailed',
    required: 'deployments.gpuSettings.required'
  }

  const gpuSelectionMessage = (selection: GpuSelection, grouped: boolean) => {
    const state =
      isCamera && grouped
        ? getGpuSelectionState(
            selection,
            isLoadingSavedVlmGpus ? undefined : devices,
            devicesLoadFailed,
            savedVlmGpus
          )
        : null

    return state ? intl.formatMessage({ id: gpuMessages[state] }) : null
  }

  const singleDevice =
    selectedDeviceIds.length === 1
      ? devices?.find((device) => device.id === selectedDeviceIds[0])
      : undefined

  const gpuIndexMessage = (grouped: boolean) =>
    isCamera &&
    !grouped &&
    singleDevice &&
    !isLoadingSavedVlmGpus &&
    getGpuSelectionError({}, singleDevice, savedVlmGpus[singleDevice.id]) ===
      'allReserved'
      ? intl.formatMessage({ id: 'deployments.gpuSettings.allReserved' })
      : null

  // For camera, we only show confidence settings for labels used in b. logic rules.
  // For media folder, b. logic is not configured, so we fall back to all model labels.
  const computedConfidenceThresholds = isCamera
    ? extractConfidenceThresholdsFromBusinessLogic(
        initialConfidenceThresholds,
        savedStreamLogics
      )
    : extractConfidenceThresholdsFromLabels(
        initialConfidenceThresholds,
        uniqueLabels
      )

  const form = useForm<FormValues>({
    initialValues: {
      confidenceThresholds: computedConfidenceThresholds,
      eventVideoDurationTime,
      preEventSeconds,
      cacheSeconds,
      inferenceInterval,
      minDetectionFrames,
      blurOptions,
      notificationSettings,
      ocrSettings,
      gpuIndex: initialGpuIndex,
      runInGroups: initialRunInGroups,
      gpuSelection: initialGpuSelection
    },

    validateInputOnBlur: true,

    validate: {
      gpuSelection: (value, values) =>
        gpuSelectionMessage(value, values.runInGroups),
      gpuIndex: (_value, values) => gpuIndexMessage(values.runInGroups),
      eventVideoDurationTime: isInRange(
        {
          min: MIN_EVENT_VIDEO_DURATION_TIME,
          max: MAX_EVENT_VIDEO_DURATION_TIME
        },
        intl.formatMessage(
          { id: 'events.video.duration.error' },
          {
            min: MIN_EVENT_VIDEO_DURATION_TIME,
            max: MAX_EVENT_VIDEO_DURATION_TIME
          }
        )
      ),
      preEventSeconds: (value, values) =>
        isCamera && values.runInGroups
          ? isInRange(
              { min: MIN_PRE_EVENT_SECONDS, max: MAX_PRE_EVENT_SECONDS },
              intl.formatMessage(
                { id: 'events.video.preEvent.error' },
                { min: MIN_PRE_EVENT_SECONDS, max: MAX_PRE_EVENT_SECONDS }
              )
            )(value)
          : null,
      cacheSeconds: (value, values) =>
        isCamera && values.runInGroups && value !== ''
          ? isInRange(
              { min: MIN_CACHE_SECONDS, max: MAX_CACHE_SECONDS },
              intl.formatMessage(
                { id: 'events.video.cache.error' },
                { min: MIN_CACHE_SECONDS, max: MAX_CACHE_SECONDS }
              )
            )(value)
          : null,
      ocrSettings: {
        whitelist: (value) =>
          validateOcrList(
            value,
            intl.formatMessage({
              id: 'deployments.ocrSettings.input.invalidCharacters'
            }),
            hasOCREnabled
          ),
        blacklist: (value) =>
          validateOcrList(
            value,
            intl.formatMessage({
              id: 'deployments.ocrSettings.input.invalidCharacters'
            }),
            hasOCREnabled
          )
      }
    }
  })

  const runInGroups = isCamera && form.getValues().runInGroups

  const groupGpuError =
    gpuSelectionMessage(form.getValues().gpuSelection, runInGroups) ?? undefined

  // form is left out of the deps on purpose: only a device or settings load may touch the selection.
  useEffect(() => {
    if (devices && !isLoadingSavedVlmGpus) {
      form.setFieldValue(
        'gpuSelection',
        sanitizeGpuSelection(
          form.getValues().gpuSelection,
          devices,
          savedVlmGpus,
          runningGpus
        )
      )
    }
  }, [devices, savedVlmGpus, isLoadingSavedVlmGpus]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinueClick = () => {
    onContinue(form.getValues())
  }

  return (
    <>
      <Title order={3} mb="md">
        <FormattedMessage id="deployments.step7" />
      </Title>

      <StepSection
        actions={
          <Group>
            <Button
              size="md"
              variant="outline"
              miw={200}
              leftSection={<IconChevronLeft size={16} stroke={3} />}
              onClick={onGoBack}
            >
              <FormattedMessage id="deployments.previousStep" />
            </Button>

            <Button
              miw={200}
              size="md"
              rightSection={<IconChevronRight size={16} stroke={3} />}
              disabled={!form.isValid()}
              onClick={handleContinueClick}
            >
              <FormattedMessage id="deployments.nextStep" />
            </Button>
          </Group>
        }
      >
        <Card mt="sm" padding="xl">
          <Grid gutter="xl">
            <Grid.Col span={6}>
              <Stack gap={48}>
                <SettingsCard
                  title={
                    <FormattedMessage id="deployments.detectionSettings.title" />
                  }
                  description={
                    <FormattedMessage id="deployments.detectionSettings.description" />
                  }
                  contentMaxWidth={480}
                >
                  <Stack gap={32}>
                    <ConfidenceSettings form={form} />
                    {isCamera && <DetectionSettings form={form} />}
                  </Stack>
                </SettingsCard>

                {isCamera && hasEmailAlertEnabled && (
                  <NotificationSettingsHandler form={form} />
                )}

                {isCamera && <EventRecordingSettings form={form} />}
              </Stack>
            </Grid.Col>

            <Grid.Col span={6}>
              <Stack gap={48}>
                {isCamera && (
                  <CameraGroupSettings form={form} error={groupGpuError} />
                )}

                {runInGroups ? (
                  <GroupGpuSettings
                    devices={isLoadingSavedVlmGpus ? undefined : devices}
                    savedVlmGpus={savedVlmGpus}
                    isLoading={isLoadingDevices || isLoadingSavedVlmGpus}
                    loadFailed={devicesLoadFailed}
                    form={form}
                    onRetry={() => {
                      void reloadDevices()
                    }}
                  />
                ) : (
                  <GpuSettings
                    deviceIds={selectedDeviceIds}
                    savedVlmGpus={savedVlmGpus[selectedDeviceIds[0]]}
                    form={form}
                  />
                )}

                {/* For media folder pipelines, standard LP/face blur is hidden -
                    users must rely on custom blur classes from their trained models. */}
                <BlurSettings
                  form={form}
                  labels={uniqueLabels}
                  showStandardBlurOptions={isCamera}
                />

                {isCamera && hasOCREnabled && <OcrSettings form={form} />}
              </Stack>
            </Grid.Col>
          </Grid>
        </Card>
      </StepSection>
    </>
  )
}
